import React, { useState } from "react";
import SearchIcon from "../../../assets/svg/search-icon";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  setCommunicationsUtmMedium,
  setCrmLeadsUtmMedium,
} from "../../../redux/slices/filterSlice";
import { useGetListingFiltersQuery } from "../../../redux/services/communicationApi";

const UtmMediumFilter = ({
  onClose,
  isCrm = false,
}: {
  onClose: () => void;
  isCrm?: boolean;
}) => {
  const selectedMediums = useSelector((state: RootState) =>
    isCrm
      ? state.filter.crmLeads.utmMedium
      : state.filter.communications.utmMedium
  );
  const duration = useSelector((state: RootState) =>
    isCrm
      ? state.filter.crmLeads.duration
      : state.filter.communications.duration
  );
  const selectedChatType = useSelector(
    (state: RootState) => state.leads.selectedChatType
  );
  const {
    data: listingFilters,
    isLoading,
    isFetching,
  } = useGetListingFiltersQuery({
    start_date: duration.startDate,
    end_date: duration.endDate,
    ...(isCrm ? {} : { chat_type: selectedChatType as string }),
  });
  const dispatch = useDispatch();
  const [selectedUtmMediums, setSelectedUtmMediums] = useState<string[]>(
    selectedMediums.value as string[]
  );
  const [searchQuery, setSearchQuery] = useState("");

  const utmMediums: string[] = listingFilters?.utm_mediums || [];
  const filteredUtmMediums = utmMediums.filter((utmMedium) =>
    utmMedium.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const handleUtmMediumSelect = (utmMedium: string) => {
    setSelectedUtmMediums((prevUtmMediums) => {
      if (prevUtmMediums.includes(utmMedium)) {
        return prevUtmMediums.filter((medium) => medium !== utmMedium);
      }
      return [...prevUtmMediums, utmMedium];
    });
  };

  const handleCancel = () => {
    setSelectedUtmMediums([]);
    setSearchQuery("");
    onClose();
  };

  const handleApply = () => {
    dispatch(
      (isCrm ? setCrmLeadsUtmMedium : setCommunicationsUtmMedium)({
        value: selectedUtmMediums,
        displayValue: selectedUtmMediums,
      })
    );
    onClose();
  };
  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-70px)]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search UTM Medium"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1">
        SELECTED ({selectedUtmMediums?.length ?? 0})
      </span>

      <div className="overflow-y-auto">
        {/* Selected Properties Display */}
        {selectedUtmMediums.length > 0 && (
          <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
            {selectedUtmMediums.map((utmMedium) => (
              <div
                key={utmMedium}
                onClick={() => handleUtmMediumSelect(utmMedium)}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {utmMedium}
                  </span>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                  <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Properties List */}
        <div className="flex flex-col">
          <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
            All Utm Mediums
          </div>

          <div className="overflow-y-auto">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {isLoading
                    ? "Loading utm mediums..."
                    : "Fetching utm mediums..."}
                </div>
              </div>
            ) : filteredUtmMediums.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {searchQuery.trim()
                    ? "No utm mediums found"
                    : "No utm mediums available"}
                </div>
              </div>
            ) : (
              filteredUtmMediums
                ?.filter(
                  (utmMedium: string) => !selectedUtmMediums.includes(utmMedium)
                )
                ?.map((utmMedium: string) => (
                  <div
                    key={utmMedium}
                    onClick={() => handleUtmMediumSelect(utmMedium)}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {utmMedium}
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-6 px-3">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={handleCancel}
        />
        <Button
          innerText="Apply"
          disabled={selectedUtmMediums.length === 0}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default UtmMediumFilter;
